/**
 * Cumulative token counter shown beside the ModelSelector.
 *
 * Reads totals from the chat store (hydrated on conversation open,
 * updated on every ``result`` WS event) and renders a muted pill
 * like ``↑ 45K · ↓ 12K``. Hover reveals the cache breakdown *and* a
 * model-wise split — how many input / output / cache tokens went to
 * each model, tagged with its provider (Anthropic, OpenRouter, …) —
 * so a chat that hopped providers can be accounted for per model.
 *
 * The pill hides itself on empty conversations — a fresh chat
 * doesn't need to announce "0 in / 0 out".
 */

import { useState } from "react";
import { useChatStore } from "@/stores/chatStore";
import type { ModelUsageEntry } from "@/api/types";
import { TokenBreakdownPanel } from "./TokenBreakdownPanel";

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// Internal provider ids (from llm_router.provider_of) → display labels.
// ``claude`` surfaces as "Anthropic" since that's the provider users know.
const PROVIDER_LABELS: Record<string, string> = {
  claude: "Anthropic",
  openrouter: "OpenRouter",
  openai: "OpenAI",
  gemini: "Google",
  unknown: "Unknown",
};

function providerLabel(provider: string | undefined): string {
  if (!provider) return "Unknown";
  return PROVIDER_LABELS[provider] ?? provider;
}

// Total input a model saw = fresh + both cache buckets (matches the
// pill's headline figure, which counts cache reads/writes as input).
function modelInputTotal(e: ModelUsageEntry): number {
  return (e.input_tokens ?? 0) + (e.cache_read_tokens ?? 0) + (e.cache_creation_tokens ?? 0);
}

export function TokenCounter() {
  const input = useChatStore((s) => s.totalInputTokens);
  const output = useChatStore((s) => s.totalOutputTokens);
  const cacheRead = useChatStore((s) => s.totalCacheReadTokens);
  const cacheCreation = useChatStore((s) => s.totalCacheCreationTokens);
  const modelUsage = useChatStore((s) => s.modelTokenUsage);
  const conversationId = useChatStore((s) => s.activeConversationId);
  const [open, setOpen] = useState(false);

  const totalIn = input + cacheRead + cacheCreation;
  if (totalIn === 0 && output === 0) return null;

  // Tooltip: a conversation-wide summary, then a per-model / per-provider
  // breakdown so a chat that ran on more than one model is accountable.
  const summaryLines = [
    `Input: ${formatTokens(totalIn)}`,
    `  • Fresh: ${formatTokens(input)}`,
    `  • Cache read: ${formatTokens(cacheRead)}`,
    cacheCreation > 0 ? `  • Cache write: ${formatTokens(cacheCreation)}` : "",
    `Output: ${formatTokens(output)}`,
  ].filter(Boolean);

  // Sort models by input volume (biggest spender first); a costed turn is
  // always present here, so an empty map just means pre-v22 history.
  const modelLines = Object.entries(modelUsage)
    .sort(([, a], [, b]) => modelInputTotal(b) - modelInputTotal(a))
    .flatMap(([model, e]) => {
      const inTotal = modelInputTotal(e);
      const cache = (e.cache_read_tokens ?? 0) + (e.cache_creation_tokens ?? 0);
      const cacheNote = cache > 0 ? ` (cache ${formatTokens(cache)})` : "";
      return [
        `${model} · ${providerLabel(e.provider)}`,
        `  ↑ ${formatTokens(inTotal)}${cacheNote} · ↓ ${formatTokens(e.output_tokens ?? 0)}`,
      ];
    });

  const tooltip = modelLines.length > 0
    ? [...summaryLines, "", "By model:", ...modelLines].join("\n")
    : summaryLines.join("\n");

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => conversationId && setOpen((v) => !v)}
        disabled={!conversationId}
        className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-space-20 text-[11px] font-medium text-space-50 tabular-nums hover:text-space transition-colors disabled:cursor-default"
        title={`${tooltip}\n\nClick for the per-model breakdown`}
        aria-label="Token usage — open breakdown"
        aria-expanded={open}
      >
        <span className="flex items-center gap-0.5" aria-label="Input tokens">
          <svg
            className="h-2.5 w-2.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
          </svg>
          {formatTokens(totalIn)}
        </span>
        <span className="text-space-30" aria-hidden>·</span>
        <span className="flex items-center gap-0.5" aria-label="Output tokens">
          <svg
            className="h-2.5 w-2.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m0 0l-7-7m7 7l7-7" />
          </svg>
          {formatTokens(output)}
        </span>
      </button>
      {open && conversationId && (
        <TokenBreakdownPanel
          conversationId={conversationId}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
